/**
 * Mirrors the SPA access token into storage for extension / embedded PSA handoff.
 *
 * - **sessionStorage**: primary (tab-scoped; preferred for security).
 * - **localStorage**: temporary dual-write so legacy browser extension builds that
 *   only read `localStorage.getItem("auth_token")` from the Theo tab still work.
 *   Remove localStorage mirroring once those installs are negligible.
 */

export const WEB_AUTH_TOKEN_KEY = "auth_token";

type AccessTokenProvider = (options?: { forceRefresh?: boolean }) => Promise<string | null>;

let accessTokenProvider: AccessTokenProvider | null = null;
let accessTokenPromise: Promise<string | null> | null = null;

export function setWebAuthTokenProvider(provider: AccessTokenProvider): void {
  accessTokenProvider = provider;
}

export function clearWebAuthTokenProvider(provider?: AccessTokenProvider): void {
  if (!provider || accessTokenProvider === provider) {
    accessTokenProvider = null;
  }
}

export function setWebAuthTokenMirror(token: string): void {
  try {
    sessionStorage.setItem(WEB_AUTH_TOKEN_KEY, token);
  } catch {
    /* ignore */
  }
  try {
    localStorage.setItem(WEB_AUTH_TOKEN_KEY, token);
  } catch {
    /* ignore quota / private mode */
  }
}

export function removeWebAuthTokenMirror(): void {
  try {
    sessionStorage.removeItem(WEB_AUTH_TOKEN_KEY);
  } catch {
    /* ignore */
  }
  try {
    localStorage.removeItem(WEB_AUTH_TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

/** Prefer sessionStorage, then legacy localStorage mirror. */
export function getWebAuthTokenMirror(): string | null {
  try {
    const s = sessionStorage.getItem(WEB_AUTH_TOKEN_KEY);
    if (s) return s;
  } catch {
    /* ignore */
  }
  try {
    return localStorage.getItem(WEB_AUTH_TOKEN_KEY);
  } catch {
    return null;
  }
}

export async function getWebAuthToken(options?: { forceRefresh?: boolean }): Promise<string | null> {
  if (!accessTokenProvider) {
    return getWebAuthTokenMirror();
  }

  if (!options?.forceRefresh && accessTokenPromise) {
    return accessTokenPromise;
  }

  accessTokenPromise = accessTokenProvider(options)
    .then((token) => {
      if (token) {
        setWebAuthTokenMirror(token);
      }
      return token || getWebAuthTokenMirror();
    })
    .finally(() => {
      accessTokenPromise = null;
    });

  return accessTokenPromise;
}
